import { useState } from 'react'

import { Box, Section } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Delete, LayersFront, Pencil } from '@factorialco/f0-react/icons/app'
import {
  EvaluationFormListItemFragment,
  GetEvaluationFormTemplatesDocument,
} from 'generated/resources/graphql'

import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import i18n from 'modules/core/lib/i18n'

import { DeleteEvaluationForm } from 'modules/ats/components/EvaluationForm/components/DeleteEvaluationForm'

import { CreateEvaluationFormTemplateSidepanel } from './CreateEvaluationFormTemplate/CreateEvaluationFormTemplateSidepanel'
import { EditEvaluationFormTemplateSidepanel } from './EditEvaluationFormTemplate/EditEvaluationFormTemplateSidepanel'

export const EvaluationForms = () => {
  const [newTemplateOpen, setNewTemplateOpen] = useState(false)
  const [editTemplateId, setEditTemplateId] = useState<number | undefined>(undefined)
  const [duplicateTemplateId, setDuplicateTemplateId] = useState<number | undefined>(undefined)
  const [deleteTemplate, setDeleteTemplate] = useState<EvaluationFormListItemFragment | undefined>(
    undefined
  )

  const source = useGraphqlDataCollectionSource({
    document: GetEvaluationFormTemplatesDocument,
    mapData: (data) => data.ats.evaluationFormsConnection.nodes,
    mapPagination: (data) => ({
      total: data.ats.evaluationFormsConnection.nodes.length,
    }),
    mapVariables: () => ({}),
    itemOnClick: (item) => () => setEditTemplateId(item.id),
    itemActions: (item) => [
      {
        label: i18n.t('common.edit'),
        icon: Pencil,
        onClick: () => setEditTemplateId(item.id),
      },
      {
        label: i18n.t('common.duplicate'),
        icon: LayersFront,
        onClick: () => setDuplicateTemplateId(item.id),
      },
      {
        label: i18n.t('common.delete'),
        icon: Delete,
        critical: true,
        onClick: () => setDeleteTemplate(item),
      },
    ],
  })

  return (
    <>
      <Section
        illustration="single-neutral-actions-process"
        title={i18n.t('ats.evaluation_forms.settings.title')}
        description={i18n.t('ats.evaluation_forms.settings.description')}
        link={{
          href: i18n.t('ats.evaluation_forms.settings.help_center_url'),
          label: i18n.t('ats.evaluation_forms.settings.how_it_works'),
        }}
        action={{
          label: i18n.t('ats.evaluation_forms.settings.new_template'),
          onClick: () => setNewTemplateOpen(true),
        }}
      />
      <Box paddingX="s64" paddingBottom="s32">
        <OneDataCollection
          source={source}
          visualizations={[
            {
              type: 'table',
              options: {
                frozenColumns: 1,
                columns: [
                  {
                    label: i18n.t('common.name'),
                    render: (item) => item.name,
                  },
                  {
                    label: i18n.t('ats.evaluation_forms.settings.questions_label'),
                    render: (item) => item.questions.map((q) => q.text).join('; '),
                  },
                ],
              },
            },
          ]}
        />
      </Box>

      {newTemplateOpen && (
        <CreateEvaluationFormTemplateSidepanel onClose={() => setNewTemplateOpen(false)} />
      )}

      {editTemplateId !== undefined && (
        <EditEvaluationFormTemplateSidepanel
          evaluationFormId={editTemplateId}
          onClose={() => setEditTemplateId(undefined)}
        />
      )}

      {duplicateTemplateId !== undefined && (
        <CreateEvaluationFormTemplateSidepanel
          evaluationFormId={duplicateTemplateId}
          duplicate
          onClose={() => setDuplicateTemplateId(undefined)}
        />
      )}

      {deleteTemplate && (
        <DeleteEvaluationForm
          evaluationForm={deleteTemplate}
          onClose={() => setDeleteTemplate(undefined)}
        />
      )}
    </>
  )
}
